import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Paper,
  CircularProgress,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  keyframes,
} from "@mui/material";
import useSWR from "swr";
import { FactoringStatus } from "components/FactoringStatus.tsx";
import { useState, useEffect, useRef } from "react";
import useImageUploadDialog from "./ImageUploadDialog";
import {
  swrKey,
  factoringListFetcher,
} from "components/factoringListApi.ts";

// Define the scroll animation
const scrollAnimation = keyframes`
  0% { transform: translateX(-50px); opacity: 0; }
  20% { opacity: 1; }
  80% { opacity: 1; }
  100% { transform: translateX(0); opacity: 0; }
`;

export default function FactoringTable() {
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isScrollable, setIsScrollable] = useState(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);

  const tableContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data, isLoading, error } = useSWR(
    [swrKey, page, rowsPerPage],
    factoringListFetcher,
  );

  // Use the image upload dialog hook
  const { openImageUploadDialog, renderImageUploadDialog } =
    useImageUploadDialog();

  // Check if table is horizontally scrollable
  useEffect(() => {
    if (!isLoading && data && data.factoringList && data.factoringList.length > 0 && isMobile) {
      const checkScrollable = () => {
        if (tableContainerRef.current) {
          const { scrollWidth, clientWidth } = tableContainerRef.current;
          const scrollable = scrollWidth > clientWidth;
          setIsScrollable(scrollable);

          if (scrollable) {
            setShowScrollIndicator(true);

            if (scrollTimerRef.current) {
              clearTimeout(scrollTimerRef.current);
            }

            // 新しいタイマーを設定
            scrollTimerRef.current = setTimeout(() => {
              setShowScrollIndicator(false);
              scrollTimerRef.current = null;
            }, 6000);
          }
        }
      };

      checkScrollable();
      window.addEventListener('resize', checkScrollable);

      return () => {
        window.removeEventListener('resize', checkScrollable);
        if (scrollTimerRef.current) {
          clearTimeout(scrollTimerRef.current);
          scrollTimerRef.current = null;
        }
      };
    } else {
      setIsScrollable(false);
      setShowScrollIndicator(false);
      if (scrollTimerRef.current) {
        clearTimeout(scrollTimerRef.current);
        scrollTimerRef.current = null;
      }
    }
  }, [isLoading, data, isMobile]);

  // Handle page change
  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle image upload button click
  const handleImageUpload = (
    factoringId: string,
    applicationNumber: string,
  ) => {
    openImageUploadDialog(factoringId, applicationNumber);
  };

  return (
    <>
      <TableContainer ref={tableContainerRef} component={Paper} sx={{ maxHeight: "80vh", position: "relative" }}>
        {showScrollIndicator && isScrollable && isMobile && (
          <Box
            sx={{
              position: "absolute",
              top: "10px",
              right: "10px",
              zIndex: 1000,
              animation: `${scrollAnimation} 2s ease-in-out infinite`,
              display: "flex",
              alignItems: "center",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              padding: "5px 10px",
              borderRadius: "4px",
              pointerEvents: "none",
            }}
          >
            <Typography variant="caption">スクロールできます →</Typography>
          </Box>
        )}
        <Table stickyHeader>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell style={{ minWidth: 120 }}>ステータス</TableCell>
              <TableCell style={{ minWidth: 120 }}>申込No.</TableCell>
              <TableCell style={{ minWidth: 120 }}>申込日</TableCell>
              <TableCell style={{ minWidth: 150 }} align="center">
                追加画像
                <br />
                アップロード
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {error && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box sx={{ mt: 2, mb: 2, color: "error.main" }}>
                    <Typography variant="h6" color="error">
                      エラーが発生しました
                    </Typography>
                    <Typography variant="body2">
                      {error.message || "データの取得中に問題が発生しました。"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {data &&
              data.factoringList &&
              data.factoringList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Box sx={{ mt: 2, mb: 2 }}>
                      <Typography variant="body1" color="text.secondary">
                        申込がありません
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

            {data &&
              data.factoringList &&
              data.factoringList.map((application) => (
                <TableRow key={application.id}>
                  <TableCell>
                    <FactoringStatus status={application.status} />
                  </TableCell>
                  <TableCell>{application.applicationNumber}</TableCell>
                  <TableCell>{application.applicationDate}</TableCell>
                  <TableCell>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        handleImageUpload(
                          application.id,
                          application.applicationNumber,
                        )
                      }
                      sx={{ fontSize: "0.7rem" }}
                    >
                      画像アップロード
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10]}
        component="div"
        count={data?.totalCount || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
      />

      {/* Render the image upload dialog */}
      {renderImageUploadDialog()}
    </>
  );
}
